import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { FileText, Upload, X } from 'lucide-react';
import { KeyKind, KeyType, KeyTypeInput, KEY_KINDS, KEY_KIND_LABELS } from '../../types/keyRegister';
import { isAcceptedTypeAttachmentFile, KEY_TYPE_ATTACHMENT_EXTENSIONS, typeAttachments } from '../../Helpers/keyRegisterUtils';
import { formatFileSize } from '../../Helpers/FileUtils';
import { useDragDrop } from '../../hooks/useDragDrop';
import KeyTypeObjectsField from './KeyTypeObjectsField';
import { KeyObjectRef, objectRefsOf, toObjectLinkMaps } from './keyObjects';

export interface BuildingOption {
    key: string;
    name: string;
}

/** The building checkboxes and pickers: named, A–Ö. */
export const toBuildingOptions = (buildings: Array<{ key: string; name?: string }> | null | undefined): BuildingOption[] =>
    (buildings ?? [])
        .map(b => ({ key: b.key, name: b.name || 'Namnlös byggnad' }))
        .sort((a, b) => a.name.localeCompare(b.name, 'sv', { numeric: true }));

/** The form's fields plus the file changes, applied after the type is saved. */
export interface KeyTypeSubmission {
    input: KeyTypeInput;
    filesToUpload: File[];
    attachmentKeysToRemove: string[];
}

interface KeyTypeModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (submission: KeyTypeSubmission) => Promise<void>;
    /** When given, the modal edits this type; otherwise it creates one. */
    type?: KeyType | null;
    buildings: BuildingOption[];
    /** A new type starts linked to this lägenhet, lokal or förråd (its Nycklar button). */
    prefillObject?: KeyObjectRef | null;
    submitting: boolean;
}

interface KeyTypeFormValues {
    name: string;
    kind: KeyKind;
    description: string;
    lockSystemRef: string;
    opensBuildings: string[];
    opensObjects: KeyObjectRef[];
    opens: string;
}

const schema = Yup.object({
    name: Yup.string().trim().required('Namn krävs').max(100, 'Max 100 tecken'),
    kind: Yup.mixed<KeyKind>().oneOf(KEY_KINDS).required(),
    description: Yup.string().trim().max(500, 'Max 500 tecken'),
    lockSystemRef: Yup.string().trim().max(100, 'Max 100 tecken'),
    opensBuildings: Yup.array(Yup.string().required()),
    opens: Yup.string().trim().max(300, 'Max 300 tecken'),
});

const toValues = (type?: KeyType | null, prefillObject?: KeyObjectRef | null): KeyTypeFormValues => ({
    name: type?.name ?? '',
    kind: type?.kind ?? 'mechanical',
    description: type?.description ?? '',
    lockSystemRef: type?.lockSystemRef ?? '',
    opensBuildings: Object.keys(type?.opensBuildings ?? {}),
    opensObjects: type ? objectRefsOf(type) : prefillObject ? [prefillObject] : [],
    opens: type?.opens ?? '',
});

const toInput = (values: KeyTypeFormValues): KeyTypeInput => ({
    name: values.name.trim(),
    kind: values.kind,
    description: values.description.trim() || undefined,
    lockSystemRef: values.lockSystemRef.trim() || undefined,
    opensBuildings: values.opensBuildings,
    ...toObjectLinkMaps(values.opensObjects),
    opens: values.opens.trim() || undefined,
});

/** Show a search field above the building list once it gets long. */
const BUILDING_SEARCH_THRESHOLD = 8;

/** Create or edit a key type (Formik + Yup). Archive and delete live in the row's menu. */
const KeyTypeModal: React.FC<KeyTypeModalProps> = ({ show, onHide, onSubmit, type, buildings, prefillObject, submitting }) => {
    const [buildingSearch, setBuildingSearch] = useState('');
    const visibleBuildings = useMemo(() => {
        const needle = buildingSearch.trim().toLowerCase();
        return needle ? buildings.filter(b => b.name.toLowerCase().includes(needle)) : buildings;
    }, [buildings, buildingSearch]);

    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [attachmentKeysToRemove, setAttachmentKeysToRemove] = useState<string[]>([]);
    const [fileError, setFileError] = useState<string | null>(null);
    const fileInput = useRef<HTMLInputElement>(null);
    const existingAttachments = useMemo(() => (type ? typeAttachments(type) : []), [type]);

    const reset = () => {
        setBuildingSearch('');
        setPendingFiles([]);
        setAttachmentKeysToRemove([]);
        setFileError(null);
    };

    const close = () => {
        reset();
        onHide();
    };

    const handleFiles = useCallback((files: File[]) => {
        const accepted = files.filter(isAcceptedTypeAttachmentFile);
        setFileError(accepted.length < files.length ? 'Endast PDF, Word, Excel eller bilder är tillåtna' : null);
        if (accepted.length > 0) setPendingFiles(prev => [...prev, ...accepted]);
    }, []);
    const { isDragOver, dragHandlers } = useDragDrop({ onFilesDropped: handleFiles });

    const toggleRemoved = (attachmentKey: string) =>
        setAttachmentKeysToRemove(prev => (prev.includes(attachmentKey) ? prev.filter(k => k !== attachmentKey) : [...prev, attachmentKey]));

    const handleSubmit = async (values: KeyTypeFormValues, helpers: FormikHelpers<KeyTypeFormValues>) => {
        helpers.setStatus(undefined);
        try {
            await onSubmit({ input: toInput(values), filesToUpload: pendingFiles, attachmentKeysToRemove });
            reset();
        } catch (err) {
            helpers.setStatus(err instanceof Error ? err.message : 'Kunde inte spara nyckeltypen');
        }
    };

    return (
        <Modal show={show} onHide={close} centered>
            <Modal.Header closeButton>
                <Modal.Title>{type ? 'Redigera nyckeltyp' : 'Ny nyckeltyp'}</Modal.Title>
            </Modal.Header>
            <Formik<KeyTypeFormValues> initialValues={toValues(type, prefillObject)} validationSchema={schema} enableReinitialize onSubmit={handleSubmit}>
                {({ values, errors, touched, status, handleChange, handleBlur, handleSubmit: submitForm, setStatus, setFieldValue }) => {
                    const toggleBuilding = (buildingKey: string) => {
                        const next = values.opensBuildings.includes(buildingKey)
                            ? values.opensBuildings.filter(k => k !== buildingKey)
                            : [...values.opensBuildings, buildingKey];
                        void setFieldValue('opensBuildings', next);
                    };
                    return (
                        <Form noValidate onSubmit={submitForm}>
                            <Modal.Body>
                                {status && <Alert variant="danger" dismissible onClose={() => setStatus(undefined)}>{status}</Alert>}

                                <Form.Group className="mb-3" controlId="key-type-name">
                                    <Form.Label>Namn *</Form.Label>
                                    <Form.Control
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.name && !!errors.name}
                                        placeholder="T.ex. Port + tvättstuga hus B"
                                        autoFocus
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Typ *</Form.Label>
                                    <div className="d-flex gap-3 flex-wrap">
                                        {KEY_KINDS.map(kind => (
                                            <Form.Check
                                                key={kind}
                                                type="radio"
                                                id={`key-type-kind-${kind}`}
                                                name="kind"
                                                value={kind}
                                                label={KEY_KIND_LABELS[kind]}
                                                checked={values.kind === kind}
                                                onChange={handleChange}
                                            />
                                        ))}
                                    </div>
                                    <Form.Text className="text-muted">Endast taggar kan spärras.</Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="key-type-opens">
                                    <Form.Label>Öppnar</Form.Label>
                                    <Form.Control
                                        name="opens"
                                        value={values.opens}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.opens && !!errors.opens}
                                        placeholder="T.ex. Port, tvättstuga, förråd"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.opens}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Byggnader</Form.Label>
                                    {buildings.length === 0 ? (
                                        <div className="text-muted">Inga byggnader.</div>
                                    ) : (
                                        <>
                                            {buildings.length > BUILDING_SEARCH_THRESHOLD && (
                                                <Form.Control
                                                    className="mb-2"
                                                    placeholder="Sök byggnad"
                                                    value={buildingSearch}
                                                    onChange={e => setBuildingSearch(e.target.value)}
                                                    aria-label="Sök byggnad"
                                                />
                                            )}
                                            <div className="key-type-buildings">
                                                {visibleBuildings.map(building => (
                                                    <Form.Check
                                                        key={building.key}
                                                        type="checkbox"
                                                        id={`key-type-building-${building.key}`}
                                                        label={building.name}
                                                        checked={values.opensBuildings.includes(building.key)}
                                                        onChange={() => toggleBuilding(building.key)}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    <Form.Text className="text-muted">För nycklar som öppnar hela byggnaden, t.ex. port eller tvättstuga.</Form.Text>
                                </Form.Group>

                                {show && (
                                    <KeyTypeObjectsField
                                        value={values.opensObjects}
                                        onChange={next => void setFieldValue('opensObjects', next)}
                                        buildings={buildings}
                                    />
                                )}

                                <Form.Group className="mb-3" controlId="key-type-lock-system">
                                    <Form.Label>Låssystem / beställningsnummer</Form.Label>
                                    <Form.Control
                                        name="lockSystemRef"
                                        value={values.lockSystemRef}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.lockSystemRef && !!errors.lockSystemRef}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.lockSystemRef}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group controlId="key-type-description">
                                    <Form.Label>Beskrivning</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="description"
                                        value={values.description}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.description && !!errors.description}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                                </Form.Group>

                                <div className={`key-type-files mt-3${isDragOver ? ' key-type-files--drag' : ''}`} {...dragHandlers}>
                                    <div className="d-flex align-items-center gap-3 mb-2">
                                        <Form.Label className="mb-0">Filer</Form.Label>
                                        <Button variant="outline-secondary" size="sm" onClick={() => fileInput.current?.click()} disabled={submitting}>
                                            <Upload size={14} className="me-1" aria-hidden="true" />Lägg till fil
                                        </Button>
                                        <input
                                            ref={fileInput}
                                            type="file"
                                            multiple
                                            hidden
                                            accept={KEY_TYPE_ATTACHMENT_EXTENSIONS.join(',')}
                                            data-testid="key-type-file-input"
                                            onChange={e => {
                                                handleFiles(Array.from(e.target.files ?? []));
                                                e.target.value = '';
                                            }}
                                        />
                                    </div>
                                    {fileError && <Alert variant="warning" className="py-1 px-2 mb-2" dismissible onClose={() => setFileError(null)}>{fileError}</Alert>}
                                    {existingAttachments.map(attachment => {
                                        const removed = attachmentKeysToRemove.includes(attachment.key);
                                        return (
                                            <div key={attachment.key} className={`key-type-file${removed ? ' key-type-file--removed' : ''}`}>
                                                <FileText size={16} className="text-muted" aria-hidden="true" />
                                                <span className="key-type-file-name">{attachment.fileName}</span>
                                                <span className="key-type-file-size">{formatFileSize(attachment.fileSize)}</span>
                                                {removed ? (
                                                    <Button variant="link" size="sm" className="p-0" onClick={() => toggleRemoved(attachment.key)}>Ångra</Button>
                                                ) : (
                                                    <Button variant="link" size="sm" className="p-0 text-danger" aria-label={`Ta bort ${attachment.fileName}`} onClick={() => toggleRemoved(attachment.key)}>
                                                        <X size={16} aria-hidden="true" />
                                                    </Button>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {pendingFiles.map((file, index) => (
                                        <div key={`pending-${index}`} className="key-type-file key-type-file--new">
                                            <Upload size={16} className="key-type-file-new-icon" aria-hidden="true" />
                                            <span className="key-type-file-name">{file.name}</span>
                                            <span className="key-type-file-size">{formatFileSize(file.size)}</span>
                                            <Button variant="link" size="sm" className="p-0 text-danger" aria-label={`Ta bort ${file.name}`} onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== index))}>
                                                <X size={16} aria-hidden="true" />
                                            </Button>
                                        </div>
                                    ))}
                                    <Form.Text className="text-muted d-block">
                                        T.ex. nyckelkvittens från låssmeden eller foto på nyckeln. PDF, Word, Excel eller bilder; du kan också dra och släppa filer här.
                                    </Form.Text>
                                </div>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={close} disabled={submitting}>Avbryt</Button>
                                <Button variant="primary" type="submit" disabled={submitting}>
                                    {submitting && <Spinner animation="border" size="sm" className="me-2" />}
                                    {type ? 'Spara' : 'Skapa nyckeltyp'}
                                </Button>
                            </Modal.Footer>
                        </Form>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default KeyTypeModal;
